import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SharePreview from '../cards/share/SharePreview';
import ImportTargetSelector from '../cards/share/ImportTargetSelector';
import RolePicker from '../cards/share/RolePicker';
import { getShare, importShare } from '@lf/api-client/lfApiShare';
import { getUserProfile } from '../api/lfApiUser';
import { getChildProfiles } from '../lib/parent/parentApi';
import {
    clearPendingShare,
    consumePendingImportAutoTrigger,
    markPendingImportAutoTrigger,
    writePendingShare,
} from '../lib/share/pendingShare';

function formatDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString();
    } catch (_) {
        return iso;
    }
}

const ShareLandingPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [share, setShare] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const [authChecked, setAuthChecked] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [childProfiles, setChildProfiles] = useState([]);
    const [autoTriggerImport, setAutoTriggerImport] = useState(false);

    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState(null);

    useEffect(() => {
        if (!token) return;
        writePendingShare(token);

        let cancelled = false;
        const load = async () => {
            const data = await getShare(token);
            if (cancelled) return;
            if (!data) {
                setNotFound(true);
                setLoading(false);
                return;
            }
            setShare(data);
            setLoading(false);

            // Probe parent auth in parallel without gating the render.
            const profile = await getUserProfile();
            if (cancelled) return;
            if (profile) {
                setUserProfile(profile);
                const children = await getChildProfiles();
                if (cancelled) return;
                setChildProfiles(children || []);
                // If the visitor clicked Import before a sign-in
                // round-trip, resume the import automatically.
                if (consumePendingImportAutoTrigger(token)) {
                    setAutoTriggerImport(true);
                }
            }
            setAuthChecked(true);
        };
        load();
        return () => { cancelled = true; };
    }, [token]);

    const onImport = async (selectedIds) => {
        if (!token || !selectedIds || selectedIds.length === 0) return;
        setImportError(null);
        setIsImporting(true);
        const result = await importShare(token, selectedIds, setImportError);
        setIsImporting(false);
        if (!result || !result.imported || result.imported.length === 0) {
            if (!importError) {
                setImportError({ message: 'Could not import. Please try again.' });
            }
            return;
        }

        clearPendingShare();
        const firstChildId = result.imported[0].child_profile_id;
        const matched = (childProfiles || []).find((c) => c.id === firstChildId);
        const username = matched && matched.username;
        if (username) {
            navigate(`/profile/${username}`);
        } else {
            navigate('/');
        }
    };

    const onAddChild = () => {
        navigate(`/add_profile?share=${encodeURIComponent(token || '')}`);
    };

    const onParentSignIn = () => {
        if (token) {
            markPendingImportAutoTrigger(token);
        }
        navigate(`/login?share=${encodeURIComponent(token || '')}`);
    };

    if (loading) {
        return (
            <div className="max-w-2xl mx-auto p-4 text-center text-gray-600">
                Loading shared study plan...
            </div>
        );
    }

    if (notFound || !share) {
        return (
            <div className="max-w-2xl mx-auto p-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <h1 className="text-xl font-semibold mb-2">Share link not found</h1>
                    <p className="text-gray-600">
                        We couldn't find a shared study plan at this URL.
                    </p>
                </div>
            </div>
        );
    }

    if (share.expired) {
        return (
            <div className="max-w-2xl mx-auto p-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <h1 className="text-xl font-semibold mb-2">
                        This share link is no longer active
                    </h1>
                    <p className="text-gray-600 mb-4">
                        Ask the person who shared it for a fresh link, or sign
                        up to create your own study plans.
                    </p>
                    <button
                        type="button"
                        className="bg-blue-500 text-white py-2 px-4 rounded"
                        onClick={() => navigate('/login')}>
                        Sign up
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto p-4">
            {!authChecked ? (
                <div className="flex justify-end mb-4 text-gray-500 text-sm">
                    Checking your account...
                </div>
            ) : userProfile ? (
                <div className="mb-4">
                    <ImportTargetSelector
                        childProfiles={childProfiles}
                        onImport={onImport}
                        isImporting={isImporting}
                        error={importError}
                        onAddChild={onAddChild}
                        autoTrigger={autoTriggerImport} />
                </div>
            ) : null}

            <SharePreview
                snapshot={share.snapshot}
                snapshotTitle={share.snapshot_title}
                sharerDisplayName={share.sharer_display_name}
                headerCta={
                    authChecked && !userProfile ? (
                        <RolePicker
                            token={token}
                            onParentSignIn={onParentSignIn} />
                    ) : null
                } />

            <div className="text-xs text-gray-500 mt-6 text-center">
                Shared on {formatDate(share.created_at)} · Powered by Learnforest
            </div>
        </div>
    );
};

export default ShareLandingPage;
